import React, { useCallback, useEffect, useState } from "react";
import {
  Card, CardBody, Button, Table, Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, Row, Col, Nav, NavItem, NavLink, Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { useAppDispatch } from "src/store";
import { MKTV2 } from "src/store/marketing-v2/saga";
import { LANGS, LANG_LABEL, Lang } from "./lang";

/**
 * Ana sayfa hero slider yönetimi (D-210).
 *
 * Diğer MarketingV2 sekmelerinden farkı: ham JSON yerine slaytın kendi
 * yapısına göre form. Slaytın mock ekranı da (RentGPT sohbeti, ürün kartları)
 * buradan yazılır — görsel yüklenmez, ekran veriden çizilir.
 *
 * Backend: /api/admin/websitev2/hero-slides · site: /api/websitev2/hero-slides
 */

type Item = any;
type L = Record<string, string>;

/** mock.jsx'teki hazır ekranlar. Yeni bir ekran eklenirse buraya da yazılır. */
const TEMPLATES: { v: string; label: string; url: string }[] = [
  { v: "overview", label: "Panel — Genel bakış", url: "app.rentificial.com/overview" },
  { v: "rentgpt", label: "RentGPT — Sohbet + kartlar", url: "rentgpt.rentificial.com" },
  { v: "rentgpt-booking", label: "RentGPT — Sohbetten ödemeye", url: "rentgpt.rentificial.com/checkout" },
  { v: "assets", label: "Varlıklar / Filo", url: "app.rentificial.com/assets" },
  { v: "reservations", label: "Rezervasyon sihirbazı", url: "app.rentificial.com/reservations" },
  { v: "contracts", label: "Sözleşmeler", url: "app.rentificial.com/contracts" },
  { v: "maintenance", label: "Bakım", url: "app.rentificial.com/maintenance" },
  { v: "finance", label: "Finans", url: "app.rentificial.com/finance" },
  { v: "customers", label: "Müşteriler", url: "app.rentificial.com/customers" },
  { v: "calendar", label: "Takvim", url: "app.rentificial.com/calendar" },
  { v: "insurance", label: "Sigorta", url: "app.rentificial.com/insurance" },
  { v: "tariffs", label: "Tarifeler", url: "app.rentificial.com/tariffs" },
  { v: "promotions", label: "Promosyonlar", url: "app.rentificial.com/promotions" },
  { v: "website", label: "Vitrin sitesi", url: "app.rentificial.com/website" },
  { v: "bundles", label: "Paket & sepet", url: "app.rentificial.com/bundles" },
];

const blankL = (): L => ({ en: "", tr: "" });
const clone = (o: any) => JSON.parse(JSON.stringify(o ?? null));

/** {en,tr,…} → tek dil. `{key:'…'}` biçimi i18n sözlüğünden gelir, düzenlenmez. */
const txt = (o: any, lang: Lang): string => {
  if (!o) return "";
  if (typeof o === "string") return o;
  if (o.key) return `⟨i18n: ${o.key}⟩`;
  return o[lang] ?? "";
};
const isKeyRef = (o: any) => !!o && typeof o === "object" && !!o.key;

/** Boş dilleri İngilizce ile doldur (site tarafı boş metin göstermesin). */
const fillLangs = (node: any): any => {
  if (Array.isArray(node)) return node.map(fillLangs);
  if (node && typeof node === "object") {
    const keys = Object.keys(node);
    const isLang = keys.length > 0 && keys.every((k) => (LANGS as readonly string[]).includes(k));
    if (isLang) {
      const en = node.en ?? node.tr ?? "";
      const o: any = {};
      LANGS.forEach((l) => { const v = node[l]; o[l] = v == null || String(v).trim() === "" ? en : v; });
      return o;
    }
    const r: any = {};
    keys.forEach((k) => { r[k] = fillLangs(node[k]); });
    return r;
  }
  return node;
};

const EMPTY_SLIDE = () => ({
  badge: blankL(),
  titleAccent: blankL(),
  titleRest: blankL(),
  lead: blankL(),
  primaryCta: { label: blankL(), go: "register", icon: "arrow-right" },
  secondaryCta: { label: blankL(), go: "features" },
  trust: [blankL()],
  mock: {},
});

const EMPTY_GPT_MOCK = () => ({
  title: { en: "RentGPT", tr: "RentGPT" },
  subtitle: blankL(),
  badge: { en: "live", tr: "canlı" },
  messages: [{ role: "user", text: blankL() }, { role: "assistant", text: blankL() }],
  cards: [{ title: blankL(), supplier: "", price: "", unit: blankL(), icon: "party-popper", tags: [blankL()] }],
  input: blankL(),
});

/** rentgpt-booking şablonunun mock alanları (sohbet şablonundan farklı). */
const EMPTY_BOOKING_MOCK = () => ({
  title: { en: "RentGPT", tr: "RentGPT" },
  badge: blankL(),
  ask: blankL(),
  answer: blankL(),
  cta: blankL(),
  input: blankL(),
});

const HeroSlidesPanel = () => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState<Lang>("tr");

  const [row, setRow] = useState<Item | null>(null);   // düz kolonlar
  const [data, setData] = useState<any>(null);         // DataJson (interleaved)

  const load = useCallback(() => {
    setLoading(true);
    dispatch(MKTV2.heroslides.list({
      payload: {
        onSuccess: (_m: string, p: Item[]) => { setRows(Array.isArray(p) ? p : []); setLoading(false); },
        onError: () => { setRows([]); setLoading(false); },
      },
    }));
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (r: Item | null) => {
    if (r) {
      setRow({ ...r });
      let d: any = {};
      try { d = JSON.parse(r.DataJson || "{}"); } catch { d = {}; }
      setData(Object.keys(d).length ? d : EMPTY_SLIDE());
    } else {
      setRow({ MockTemplate: "overview", BrowserUrl: "app.rentificial.com/overview", Order: rows.length + 1, IsActive: true });
      setData(EMPTY_SLIDE());
    }
    setLang("tr");
    setOpen(true);
  };

  const save = () => {
    if (!row || !data) return;
    const body: any = {
      ...(row.Id ? { Id: row.Id } : {}),
      MockTemplate: row.MockTemplate || "overview",
      BrowserUrl: row.BrowserUrl || "",
      Order: Number(row.Order) || 0,
      IsActive: !!row.IsActive,
      DataJson: JSON.stringify(fillLangs(data)),
    };
    setSaving(true);
    const ok = () => { setSaving(false); setOpen(false); toast.success("Kaydedildi"); load(); };
    const err = () => setSaving(false);
    dispatch(row.Id
      ? MKTV2.heroslides.update({ id: row.Id, body, payload: { onSuccess: ok, onError: err } })
      : MKTV2.heroslides.create({ body, payload: { onSuccess: ok, onError: err } }));
  };

  const del = (r: Item) => {
    if (!window.confirm("Bu slaytı silmek istiyor musunuz?")) return;
    dispatch(MKTV2.heroslides.del({ id: r.Id, payload: { onSuccess: () => { toast.success("Silindi"); load(); }, onError: () => {} } }));
  };

  /** Sıra değiştirme: iki kaydın Order'ı takas edilir (site Order'a göre çizer). */
  const move = (r: Item, dir: -1 | 1) => {
    const sorted = [...rows].sort((a, b) => a.Order - b.Order);
    const i = sorted.findIndex((x) => x.Id === r.Id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= sorted.length) return;
    const a = sorted[i], b = sorted[j];
    const put = (x: Item, order: number, onDone: () => void) =>
      dispatch(MKTV2.heroslides.update({
        id: x.Id,
        body: { Id: x.Id, MockTemplate: x.MockTemplate, BrowserUrl: x.BrowserUrl, Order: order, IsActive: x.IsActive, DataJson: x.DataJson },
        payload: { onSuccess: onDone, onError: () => {} },
      }));
    put(a, b.Order, () => put(b, a.Order, load));
  };

  // ---- form yardımcıları -------------------------------------------------
  const setD = (fn: (d: any) => void) => setData((prev: any) => { const n = clone(prev); fn(n); return n; });
  const setL = (path: (d: any) => any, key: string, v: string) =>
    setD((d) => { const t = path(d); if (t) { if (!t[key] || typeof t[key] !== "object") t[key] = blankL(); t[key][lang] = v; } });

  /* Bileşen DEĞİL, düz fonksiyon: her render'da yeni bir bileşen tipi üretilirse
     React input'u unmount/remount eder ve her tuşta odak kaybolur. */
  const lf = (label: string, get: () => any, set: (v: string) => void, area?: boolean) => {
    const cur = get();
    return (
      <div className="mb-3" key={label}>
        <Label className="mb-1">{label}</Label>
        {isKeyRef(cur) ? (
          <>
            <Input value={txt(cur, lang)} disabled />
            <small className="text-muted">Sözlükten geliyor ({cur.key}) — 8 dil otomatik.</small>
          </>
        ) : (
          <Input type={area ? "textarea" : "text"} style={area ? { minHeight: 84 } : undefined}
            value={(cur || {})[lang] ?? ""} onChange={(e: any) => set(e.target.value)} />
        )}
      </div>
    );
  };

  const isGptChat = row?.MockTemplate === "rentgpt";        // balon + kart listesi
  const isGptBooking = row?.MockTemplate === "rentgpt-booking"; // tek soru/cevap + ödeme kutusu
  const isGpt = isGptChat || isGptBooking;
  const mock = data?.mock || {};

  const ensureGptMock = () => setD((d) => { if (!d.mock || !d.mock.messages) d.mock = EMPTY_GPT_MOCK(); });
  const ensureBookingMock = () => setD((d) => { if (!d.mock || !d.mock.ask) d.mock = EMPTY_BOOKING_MOCK(); });

  const sortedRows = [...rows].sort((a, b) => a.Order - b.Order);

  return (
    <>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-0">Hero Slaytları <span className="text-muted">({rows.length})</span></h5>
              <small className="text-muted">Ana sayfadaki üst bölüm. Tek slayt varsa slider görünmez, bugünkü hero gibi çalışır.</small>
            </div>
            <div>
              <Button color="light" className="me-2" onClick={load}><i className="bx bx-refresh"></i> Yenile</Button>
              <Button color="primary" onClick={() => openEdit(null)}><i className="bx bx-plus"></i> Yeni slayt</Button>
            </div>
          </div>

          <div className="table-responsive">
            <Table className="table-nowrap align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Sıra</th>
                  <th>Başlık (TR)</th>
                  <th>Mock ekran</th>
                  <th>Adres</th>
                  <th style={{ width: 70 }}>Aktif</th>
                  <th style={{ width: 120 }} className="text-end">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Yükleniyor…</td></tr>
                ) : sortedRows.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Slayt yok.</td></tr>
                ) : sortedRows.map((r, i) => {
                  let d: any = {}; try { d = JSON.parse(r.DataJson || "{}"); } catch { /* bozuk JSON: satır yine listelensin */ }
                  const tpl = TEMPLATES.find((x) => x.v === r.MockTemplate);
                  return (
                    <tr key={r.Id}>
                      <td>
                        <span className="me-2">{r.Order}</span>
                        <Button size="sm" color="light" disabled={i === 0} onClick={() => move(r, -1)}><i className="bx bx-up-arrow-alt"></i></Button>
                        <Button size="sm" color="light" disabled={i === sortedRows.length - 1} onClick={() => move(r, 1)}><i className="bx bx-down-arrow-alt"></i></Button>
                      </td>
                      <td className="fw-medium">
                        {(d.titleAccent?.tr || "") + " " + (d.titleRest?.tr || "") || "—"}
                      </td>
                      <td>{tpl ? tpl.label : r.MockTemplate}{r.MockTemplate === "rentgpt" && <Badge color="info" className="ms-2">RentGPT</Badge>}</td>
                      <td className="text-muted">{r.BrowserUrl}</td>
                      <td>{r.IsActive ? <i className="text-success bx bx-check-square"></i> : <i className="text-danger mdi mdi-close-box-outline"></i>}</td>
                      <td className="text-end">
                        <Button size="sm" color="light" className="me-1" onClick={() => openEdit(r)}><i className="bx bx-pencil"></i></Button>
                        <Button size="sm" color="light" onClick={() => del(r)}><i className="bx bx-trash text-danger"></i></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={open} toggle={() => setOpen(false)} size="xl" scrollable>
        <ModalHeader toggle={() => setOpen(false)}>{row?.Id ? "Slaytı düzenle" : "Yeni slayt"}</ModalHeader>
        <ModalBody>
          {row && data && (
            <Row className="g-3">
              <Col md={7}>
                <Row className="g-3 mb-3">
                  <Col md={5}>
                    <Label>Mock ekran</Label>
                    <Input type="select" value={row.MockTemplate}
                      onChange={(e: any) => {
                        const v = e.target.value;
                        const tpl = TEMPLATES.find((x) => x.v === v);
                        setRow((r: Item) => ({ ...r, MockTemplate: v, BrowserUrl: tpl ? tpl.url : r.BrowserUrl }));
                        if (v === "rentgpt") ensureGptMock();
                        if (v === "rentgpt-booking") ensureBookingMock();
                      }}>
                      {TEMPLATES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Label>Tarayıcı adresi</Label>
                    <Input value={row.BrowserUrl ?? ""} onChange={(e: any) => setRow((r: Item) => ({ ...r, BrowserUrl: e.target.value }))} />
                  </Col>
                  <Col md={2}>
                    <Label>Sıra</Label>
                    <Input type="number" value={row.Order ?? 0} onChange={(e: any) => setRow((r: Item) => ({ ...r, Order: e.target.value }))} />
                  </Col>
                  <Col md={1} className="d-flex align-items-end">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="hs-active" checked={!!row.IsActive}
                        onChange={(e: any) => setRow((r: Item) => ({ ...r, IsActive: e.target.checked }))} />
                      <label className="form-check-label" htmlFor="hs-active">Aktif</label>
                    </div>
                  </Col>
                </Row>

                <Nav pills className="mb-3 flex-wrap gap-1">
                  {LANGS.map((l) => (
                    <NavItem key={l}>
                      <NavLink href="#" active={lang === l} className="py-1 px-2"
                        onClick={(e: any) => { e.preventDefault(); setLang(l); }}>{LANG_LABEL[l]}</NavLink>
                    </NavItem>
                  ))}
                </Nav>
                <small className="text-muted d-block mb-3">Boş bırakılan diller kayıtta İngilizce ile doldurulur.</small>

                {lf("Rozet metni", () => data.badge, (v) => setL((d) => d, "badge", v))}
                {lf("Başlık — vurgulu kısım", () => data.titleAccent, (v) => setL((d) => d, "titleAccent", v))}
                {lf("Başlık — devamı", () => data.titleRest, (v) => setL((d) => d, "titleRest", v))}
                {lf("Alt metin", () => data.lead, (v) => setL((d) => d, "lead", v), true)}

                <Row className="g-2">
                  <Col md={6}>
                    {lf("Ana buton", () => data.primaryCta?.label, (v) => setL((d) => d.primaryCta, "label", v))}
                    <Input className="mb-2" placeholder="hedef sayfa (register, features…)" value={data.primaryCta?.go ?? ""}
                      onChange={(e: any) => setD((d) => { d.primaryCta = { ...(d.primaryCta || {}), go: e.target.value }; })} />
                    <Input className="mb-1" placeholder="dış adres — {RENTGPT_URL} kullanılabilir" value={data.primaryCta?.href ?? ""}
                      onChange={(e: any) => setD((d) => { d.primaryCta = { ...(d.primaryCta || {}), href: e.target.value }; })} />
                    <small className="text-muted d-block mb-3">Dış adres doluysa buton oraya gider. <code>{"{RENTGPT_URL}"}</code> ortama göre çözülür (stage → rentgptstage).</small>
                  </Col>
                  <Col md={6}>
                    {lf("İkincil buton", () => data.secondaryCta?.label, (v) => setL((d) => d.secondaryCta, "label", v))}
                    <Input className="mb-2" placeholder="hedef sayfa" value={data.secondaryCta?.go ?? ""}
                      onChange={(e: any) => setD((d) => { d.secondaryCta = { ...(d.secondaryCta || {}), go: e.target.value }; })} />
                    <Input className="mb-3" placeholder="dış adres (opsiyonel)" value={data.secondaryCta?.href ?? ""}
                      onChange={(e: any) => setD((d) => { d.secondaryCta = { ...(d.secondaryCta || {}), href: e.target.value }; })} />
                  </Col>
                </Row>

                <Label className="mb-1">Güven satırı (tik'li kısa maddeler)</Label>
                {(data.trust || []).map((tItem: any, i: number) => (
                  <div className="d-flex gap-2 mb-2" key={i}>
                    <Input value={isKeyRef(tItem) ? txt(tItem, lang) : (tItem?.[lang] ?? "")} disabled={isKeyRef(tItem)}
                      onChange={(e: any) => setD((d) => { if (!d.trust[i] || isKeyRef(d.trust[i])) return; d.trust[i][lang] = e.target.value; })} />
                    <Button color="light" onClick={() => setD((d) => { d.trust.splice(i, 1); })}><i className="bx bx-trash text-danger"></i></Button>
                  </div>
                ))}
                <Button size="sm" color="light" className="mb-3" onClick={() => setD((d) => { d.trust = [...(d.trust || []), blankL()]; })}>+ Madde ekle</Button>

                {isGpt && (
                  <>
                    <hr />
                    <h6 className="mb-2">RentGPT mock ekranı</h6>
                    {lf("Ekran başlığı", () => mock.title, (v) => setL((d) => d.mock, "title", v))}
                    {lf("Ekran alt başlığı", () => mock.subtitle, (v) => setL((d) => d.mock, "subtitle", v))}
                    {lf("Durum rozeti", () => mock.badge, (v) => setL((d) => d.mock, "badge", v))}

                    {isGptBooking && (
                      <>
                        {lf("Müşterinin sorusu", () => mock.ask, (v) => setL((d) => d.mock, "ask", v), true)}
                        {lf("RentGPT’nin cevabı", () => mock.answer, (v) => setL((d) => d.mock, "answer", v), true)}
                        {lf("Ödeme butonu", () => mock.cta, (v) => setL((d) => d.mock, "cta", v))}
                      </>
                    )}

                    {isGptChat && (
                    <>
                    <Label className="mb-1">Sohbet balonları</Label>
                    {(mock.messages || []).map((m: any, i: number) => (
                      <div className="d-flex gap-2 mb-2" key={i}>
                        <Input type="select" style={{ maxWidth: 130 }} value={m.role}
                          onChange={(e: any) => setD((d) => { d.mock.messages[i].role = e.target.value; })}>
                          <option value="user">Müşteri</option>
                          <option value="assistant">RentGPT</option>
                        </Input>
                        <Input type="textarea" style={{ minHeight: 58 }} value={m.text?.[lang] ?? ""}
                          onChange={(e: any) => setD((d) => { d.mock.messages[i].text = { ...(d.mock.messages[i].text || {}), [lang]: e.target.value }; })} />
                        <Button color="light" onClick={() => setD((d) => { d.mock.messages.splice(i, 1); })}><i className="bx bx-trash text-danger"></i></Button>
                      </div>
                    ))}
                    <Button size="sm" color="light" className="mb-3"
                      onClick={() => setD((d) => { d.mock = d.mock || {}; d.mock.messages = [...(d.mock.messages || []), { role: "assistant", text: blankL() }]; })}>+ Balon ekle</Button>

                    <Label className="mb-1">Ürün kartları</Label>
                    {(mock.cards || []).map((c: any, i: number) => (
                      <Card className="mb-2" key={i}><CardBody className="p-2">
                        <Row className="g-2">
                          <Col md={6}><Input placeholder="Ürün adı" value={c.title?.[lang] ?? ""}
                            onChange={(e: any) => setD((d) => { d.mock.cards[i].title = { ...(d.mock.cards[i].title || {}), [lang]: e.target.value }; })} /></Col>
                          <Col md={6}><Input placeholder="Tedarikçi" value={c.supplier ?? ""}
                            onChange={(e: any) => setD((d) => { d.mock.cards[i].supplier = e.target.value; })} /></Col>
                          <Col md={3}><Input placeholder="Fiyat ($249)" value={c.price ?? ""}
                            onChange={(e: any) => setD((d) => { d.mock.cards[i].price = e.target.value; })} /></Col>
                          <Col md={3}><Input placeholder="Birim (/ gün)" value={c.unit?.[lang] ?? ""}
                            onChange={(e: any) => setD((d) => { d.mock.cards[i].unit = { ...(d.mock.cards[i].unit || {}), [lang]: e.target.value }; })} /></Col>
                          <Col md={3}><Input placeholder="İkon (lucide)" value={c.icon ?? ""}
                            onChange={(e: any) => setD((d) => { d.mock.cards[i].icon = e.target.value; })} /></Col>
                          <Col md={3}><Input placeholder="Etiketler (virgülle)"
                            value={(c.tags || []).map((tg: any) => tg?.[lang] ?? "").join(", ")}
                            onChange={(e: any) => setD((d) => {
                              const parts = e.target.value.split(",").map((x: string) => x.trim()).filter(Boolean);
                              const old = d.mock.cards[i].tags || [];
                              d.mock.cards[i].tags = parts.map((v: string, k: number) => ({ ...(old[k] || blankL()), [lang]: v }));
                            })} /></Col>
                        </Row>
                        <div className="text-end mt-1">
                          <Button size="sm" color="light" onClick={() => setD((d) => { d.mock.cards.splice(i, 1); })}><i className="bx bx-trash text-danger"></i> Kartı sil</Button>
                        </div>
                      </CardBody></Card>
                    ))}
                    <Button size="sm" color="light" className="mb-3"
                      onClick={() => setD((d) => { d.mock = d.mock || {}; d.mock.cards = [...(d.mock.cards || []), { title: blankL(), supplier: "", price: "", unit: blankL(), icon: "package", tags: [] }]; })}>+ Kart ekle</Button>

                    </>
                    )}

                    {lf("Giriş alanı yer tutucusu", () => mock.input, (v) => setL((d) => d.mock, "input", v))}
                  </>
                )}
              </Col>

              {/* ---- canlı önizleme ---- */}
              <Col md={5}>
                <div style={{ position: "sticky", top: 0 }}>
                  <Label className="mb-1">Önizleme ({LANG_LABEL[lang]})</Label>
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, background: "#fff" }}>
                    <span style={{ display: "inline-block", font: "600 11px system-ui", color: "#2e78bc", background: "#eaf2fb", padding: "4px 10px", borderRadius: 999 }}>
                      {txt(data.badge, lang) || "rozet"}
                    </span>
                    <h4 style={{ margin: "10px 0 8px", lineHeight: 1.25, fontSize: 20 }}>
                      <span style={{ color: "#2e78bc" }}>{txt(data.titleAccent, lang)}</span> {txt(data.titleRest, lang)}
                    </h4>
                    <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 10 }}>{txt(data.lead, lang)}</p>
                    <div className="d-flex gap-2 mb-2">
                      <span style={{ font: "600 12px system-ui", color: "#fff", background: "#2e78bc", padding: "6px 12px", borderRadius: 8 }}>{txt(data.primaryCta?.label, lang) || "buton"}</span>
                      <span style={{ font: "600 12px system-ui", color: "#374151", border: "1px solid #d1d5db", padding: "6px 12px", borderRadius: 8 }}>{txt(data.secondaryCta?.label, lang) || "buton"}</span>
                    </div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", color: "#6b7280", fontSize: 11.5, marginBottom: 12 }}>
                      {(data.trust || []).map((tItem: any, i: number) => <span key={i}>✓ {txt(tItem, lang)}</span>)}
                    </div>

                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                      <div style={{ background: "#f3f4f6", padding: "5px 9px", font: "500 10.5px system-ui", color: "#6b7280" }}>🔒 {row.BrowserUrl}</div>
                      {isGpt ? (
                        <div style={{ padding: 10, background: "#fff" }}>
                          <div style={{ font: "700 12px system-ui" }}>{txt(mock.title, lang) || "RentGPT"}</div>
                          <div style={{ font: "500 10.5px system-ui", color: "#9ca3af", marginBottom: 8 }}>{txt(mock.subtitle, lang)}</div>
                          {isGptBooking && (
                            <>
                              <div style={{ maxWidth: "85%", marginLeft: "auto", marginBottom: 6, padding: "6px 10px", borderRadius: 10, fontSize: 11.5, background: "#2e78bc", color: "#fff" }}>{txt(mock.ask, lang)}</div>
                              <div style={{ maxWidth: "85%", marginBottom: 6, padding: "6px 10px", borderRadius: 10, fontSize: 11.5, background: "#f3f4f6", color: "#111827" }}>{txt(mock.answer, lang)}</div>
                              <div style={{ marginTop: 8, textAlign: "center", background: "#2e78bc", color: "#fff", borderRadius: 8, padding: "7px 10px", font: "600 11.5px system-ui" }}>{txt(mock.cta, lang)}</div>
                            </>
                          )}
                          {(mock.messages || []).map((m: any, i: number) => (
                            <div key={i} style={{
                              maxWidth: "85%", marginLeft: m.role === "user" ? "auto" : 0, marginBottom: 6,
                              padding: "6px 10px", borderRadius: 10, fontSize: 11.5,
                              background: m.role === "user" ? "#2e78bc" : "#f3f4f6",
                              color: m.role === "user" ? "#fff" : "#111827",
                            }}>{txt(m.text, lang)}</div>
                          ))}
                          {(mock.cards || []).map((c: any, i: number) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 8px", marginTop: 6 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ font: "700 11.5px system-ui" }}>{txt(c.title, lang)}</div>
                                <div style={{ font: "500 10.5px system-ui", color: "#9ca3af" }}>{c.supplier}</div>
                                <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
                                  {(c.tags || []).map((tg: any, j: number) => (
                                    <span key={j} style={{ font: "600 9.5px system-ui", background: "#f3f4f6", color: "#4b5563", padding: "2px 6px", borderRadius: 999 }}>{txt(tg, lang)}</span>
                                  ))}
                                </div>
                              </div>
                              <div style={{ textAlign: "right", font: "700 12px system-ui" }}>{c.price}
                                <div style={{ font: "500 10px system-ui", color: "#9ca3af" }}>{txt(c.unit, lang)}</div>
                              </div>
                            </div>
                          ))}
                          <div style={{ marginTop: 8, border: "1px solid #d1d5db", borderRadius: 999, padding: "6px 10px", font: "500 11px system-ui", color: "#9ca3af" }}>{txt(mock.input, lang)}</div>
                        </div>
                      ) : (
                        <div style={{ padding: 24, textAlign: "center", color: "#9ca3af", font: "500 12px system-ui" }}>
                          {TEMPLATES.find((x) => x.v === row.MockTemplate)?.label || row.MockTemplate}
                          <div style={{ fontSize: 11, marginTop: 4 }}>Hazır ekran — içeriği sabittir.</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2">Önizleme yaklaşıktır; gerçek görünüm sitenin kendi tipografisiyle çizilir.</small>
                </div>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setOpen(false)}>Kapat</Button>
          <Button color="primary" disabled={saving} onClick={save}>{saving ? "Kaydediliyor…" : "Kaydet"}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default HeroSlidesPanel;
